import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RangeValue } from "@/shared/ui";
import { toggleSort, type SortConfig, type Locale } from "@/shared/utils";
import { usePagination } from "@/shared/hooks";
import { useGetItemRequestsQuery } from "../api";
import type { ItemRequestListItem, ItemRequestStatus } from "../types";

export type RequestSortKey = "itemName" | "status" | "quantity" | "createdAt";

export interface ItemRequestsStats {
  totalRequests: number;
  activeRequests: number;
  completedRequests: number;
}

export interface UseItemRequestsListPageReturn {
  data: ItemRequestListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  search: string;
  setSearch: (value: string) => void;
  statusFilter: ItemRequestStatus | null;
  setStatusFilter: (value: ItemRequestStatus | null) => void;
  dateRange: RangeValue | null;
  setDateRange: (value: RangeValue | null) => void;
  activeFilters: boolean;
  handleResetFilters: () => void;
  filteredItems: ItemRequestListItem[];
  sortConfig: SortConfig<RequestSortKey> | null;
  handleSort: (key: RequestSortKey) => void;
  sortedItems: ItemRequestListItem[];
  pagination: ReturnType<typeof usePagination>;
  pageItems: ItemRequestListItem[];
  stats: ItemRequestsStats;
  locale: Locale;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useItemRequestsListPage(namespace = "WarehousePage"): UseItemRequestsListPageReturn {
  const { i18n, t } = useTranslation(namespace);
  const locale = i18n.language as Locale;

  const { data, isLoading, isError } = useGetItemRequestsQuery();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ItemRequestStatus | null>(null);
  const [dateRange, setDateRange] = useState<RangeValue | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig<RequestSortKey> | null>(null);

  const activeFilters = useMemo(() => {
    return !!search.trim() || !!statusFilter || !!(dateRange?.start || dateRange?.end);
  }, [search, statusFilter, dateRange]);

  const filteredItems = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.filter((item) => {
      if (item.status === "pending") return false;

      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          item.item_name.toLowerCase().includes(searchLower) ||
          item.requested_by_name.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (statusFilter && item.status !== statusFilter) {
        return false;
      }

      if (dateRange?.start || dateRange?.end) {
        const itemDate = new Date(item.created_at);
        if (dateRange.start && itemDate < dateRange.start) return false;
        if (dateRange.end) {
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          if (itemDate > endOfDay) return false;
        }
      }

      return true;
    });
  }, [data, search, statusFilter, dateRange]);

  function handleSort(key: RequestSortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  const sortedItems = useMemo(() => {
    if (!sortConfig) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const { key, direction } = sortConfig;
      const mul = direction === "asc" ? 1 : -1;

      switch (key) {
        case "itemName":
          return mul * a.item_name.localeCompare(b.item_name);
        case "status":
          return mul * a.status.localeCompare(b.status);
        case "quantity":
          return mul * (a.quantity - b.quantity);
        case "createdAt":
          return mul * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        default:
          return 0;
      }
    });
  }, [filteredItems, sortConfig]);

  const stats = useMemo<ItemRequestsStats>(() => {
    if (!data || !Array.isArray(data)) return { totalRequests: 0, activeRequests: 0, completedRequests: 0 };
    const visible = data.filter((r) => r.status !== "pending");
    return {
      totalRequests: visible.length,
      activeRequests: visible.filter((r) => r.status === "approved" || r.status === "fulfilling").length,
      completedRequests: visible.filter((r) => r.status === "fulfilled").length,
    };
  }, [data]);

  const pagination = usePagination(sortedItems.length, 10, [search, statusFilter, dateRange]);
  const pageItems = sortedItems.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setStatusFilter(null);
    setDateRange(null);
  }

  return {
    data,
    isLoading,
    isError,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    activeFilters,
    handleResetFilters,
    filteredItems,
    sortConfig,
    handleSort,
    sortedItems,
    pagination,
    pageItems,
    stats,
    locale,
    t,
  };
}
